import { Box, Button, Container, Paper, Stack, Typography, CircularProgress, Divider, TextField, InputAdornment, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import DescriptionIcon from '@mui/icons-material/Description';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ReplayIcon from '@mui/icons-material/Replay';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import { useMemo, useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useGetAltPayOptionsQuery, useRetryPaymentMutation, useVerifyVpaMutation } from '../services/paymentApi';
import { useSnackbar } from 'notistack';

const AUTH_MODES = [
  { value: 'DCB', label: 'Net Banking', icon: <AccountBalanceIcon fontSize="small" /> },
  { value: 'DC', label: 'Debit Card', icon: <CreditCardIcon fontSize="small" /> },
  { value: 'UPI', label: 'UPI', icon: <QrCode2Icon fontSize="small" /> },
  { value: 'AADHAR', label: 'Aadhar', icon: <DescriptionIcon fontSize="small" /> },
  { value: 'PAN', label: 'PAN', icon: <DescriptionIcon fontSize="small" /> },
];



function useQuery() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

export default function PaymentCallback() {
  const query = useQuery();
  const navigate = useNavigate();

  const status = (query.get('status') || '').toLowerCase();
  const type = query.get('type') || undefined;
  const message = query.get('message') || undefined;
  const refNo = query.get('refNo') || undefined;
  const fundCode = query.get('fund') || undefined;
  const ihNoOrCart = query.get('ihNoOrCart') || undefined;
  const returnTo = query.get('returnTo') || undefined;
  const amount = query.get('amount') || undefined;

  const isSuccess = status === 'success';
  const heading = isSuccess ? 'Payment Successful' : 'Payment Failed';
  const color = isSuccess ? 'success.main' : 'error.main';
  const Icon = isSuccess ? CheckCircleOutlineIcon : ErrorOutlineIcon;

  const { data: altPayData, isLoading: isLoadingAltOptions } = useGetAltPayOptionsQuery(
    { refNo: refNo!, fund: fundCode! },
    { skip: isSuccess || !refNo || !fundCode }
  );

  const prevOption = useMemo(() => altPayData?.options?.find(opt => opt.label === "Previous Option"), [altPayData]);
  const newOptions = useMemo(() => altPayData?.options?.filter(opt => opt.label === "New Option") || [], [altPayData]);

  const [retryPayment, { isLoading: isRetrying }] = useRetryPaymentMutation();
  const [verifyVpa, { isLoading: isVerifyingVpa }] = useVerifyVpaMutation();
  const [upiId, setUpiId] = useState<string>('');
  const [isVpaVerified, setIsVpaVerified] = useState<boolean>(false);
  const [vpaAttempted, setVpaAttempted] = useState<boolean>(false);
  const [_selectedOption, setSelectedOption] = useState<string | null>(null);
  const [timeLeft, setTimeLeft] = useState<number | null>(null);
  const { enqueueSnackbar } = useSnackbar();

  // Confirmation dialog state
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmAction, setConfirmAction] = useState<(() => void) | null>(null);
  const [confirmMessage, setConfirmMessage] = useState('');

  const handleBack = () => {
    if (returnTo) {
      window.location.href = (returnTo);
    } else {
      navigate(-1);
    }
  };

  useEffect(() => {
    if (altPayData?.time !== undefined) {
      setTimeLeft(altPayData.time);
    }
  }, [altPayData]);

  useEffect(() => {
    if (timeLeft === 0) {
      handleBack();
      return;
    }

    if (timeLeft === null || timeLeft < 0) return;

    const timer = setInterval(() => {
      setTimeLeft((prev) => (prev !== null && prev > 0 ? prev - 1 : 0));
    }, 1000);

    return () => clearInterval(timer);
  }, [timeLeft]);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const isTimerExpired = timeLeft === 0;

  const handleUpiInputChange = (val: string) => {
    setUpiId(val);
    setIsVpaVerified(false);
    setVpaAttempted(false);
  };

  const showConfirmation = (message: string, action: () => void) => {
    setConfirmMessage(message);
    setConfirmAction(() => action);
    setConfirmOpen(true);
  };

  const handleConfirmClose = () => {
    setConfirmOpen(false);
    setConfirmAction(null);
    setConfirmMessage('');
  };

  const handleConfirmProceed = () => {
    if (confirmAction) confirmAction();
    handleConfirmClose();
  };



  const handleRetry = async (authModeLabel: string) => {
    if (isTimerExpired) return;
    try {
      const mode = AUTH_MODES.find(m => m.label === authModeLabel) || { value: authModeLabel };
      const payload = {
        ihNoOrCart: ihNoOrCart || refNo || '',
        fund: fundCode || '',
        newAuthMode: mode.value,
        upiVPA: upiId || '',
      };

      const response = await retryPayment(payload).unwrap();
      if (response.paymentLink) {
        window.location.href = response.paymentLink;
      }
    } catch (error: any) {
      console.error('Retry failed:', error);
      enqueueSnackbar(error?.data?.message || 'Payment retry failed. Please try again.', {
        variant: 'error',
      });
    }
  };

  const handleVerifyAndPay = async (option: any) => {
    if (isTimerExpired || !upiId) return;

    try {
      if (!isVpaVerified) {
        // Step 1: Verify
        const response = await verifyVpa({ vpa: upiId }).unwrap();
        setVpaAttempted(true);
        if (response.status === 'success') {
          setIsVpaVerified(true);
        } else {
          setIsVpaVerified(false);
        }
        return; // Wait for user to click again to Pay
      }

      // Step 2: Pay
      setSelectedOption(option.id);
      const mode = AUTH_MODES.find(m => m.label === option.authMode) || { value: option.authMode };
      const payload = {
        ihNoOrCart: ihNoOrCart || refNo || '',
        fund: fundCode || '',
        newAuthMode: mode.value,
        upiVPA: upiId,
      };

      const response = await retryPayment(payload).unwrap();
      if (response.paymentLink) {
        window.location.href = response.paymentLink;
      }
    } catch (error: any) {
      console.error('VPA Action failed:', error);
      enqueueSnackbar(error?.data?.message || 'Action failed. Please try again.', {
        variant: 'error',
      });
      if (!isVpaVerified) setVpaAttempted(true);
    }
  };

  const handleOptionClick = (option: any) => {
    if (isTimerExpired) return;
    showConfirmation(
      `Are you sure you want to retry payment via ${option.authMode}?`,
      () => {
        setSelectedOption(option.id);
        handleRetry(option.authMode);
      }
    );
  };

  return (
    <Container maxWidth="sm" sx={{ py: 6 }}>
      <Paper elevation={0} sx={{ p: 4, borderRadius: 4, boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.05)', border: '1px solid #f0f0f0' }}>
        <Stack spacing={3} alignItems="center" textAlign="center">
          <Box sx={{
            width: 64, height: 64, borderRadius: '50%', bgcolor: isSuccess ? '#e8f5e9' : '#FFF5F5', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1
          }}>
            <Icon sx={{ fontSize: 32, color }} />
          </Box>

          <Box>
            <Typography variant="h5" fontWeight={700} gutterBottom>
              {heading}
            </Typography>
            <Stack spacing={0.5} alignItems="center">
              {refNo && (
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.9rem' }}>
                  Reference No: <Box component="span" fontWeight={600} color="text.primary">{refNo}</Box>
                </Typography>
              )}
              {type && (
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.9rem' }}>
                  Type: <Box component="span" fontWeight={600} color="text.primary">{type}</Box>
                </Typography>
              )}
              {amount && status === 'success' && (
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.9rem' }}>
                  Amount Invested: <Box component="span" fontWeight={600} color="text.primary"> ₹ {amount}</Box>
                </Typography>
              )}
              {message && status !== 'success' && (
                <Box sx={{ mt: 1.5, display: 'inline-flex', alignItems: 'center', bgcolor: isSuccess ? 'success.lighter' : '#FFF5F5', px: 2, py: 0.5, borderRadius: 4 }}>
                  <InfoOutlinedIcon sx={{ fontSize: 16, mr: 0.5, color: isSuccess ? 'success.main' : 'error.main' }} />
                  <Typography variant="caption" fontWeight={500} color={isSuccess ? 'success.main' : 'error.main'}>
                    {message.toLowerCase()}
                  </Typography>
                </Box>
              )}
            </Stack>
          </Box>


          <Stack direction="row" spacing={2} width="100%" alignItems="center" justifyContent="space-between" sx={{ pt: 1 }}>
            {status !== 'success' && (
              <Button
                variant="text"
                onClick={handleBack}
                sx={{ flex: 1, color: 'primary.main', borderRadius: 50, py: 1.2, fontWeight: 600, textTransform: 'none' }}
              >
                Cancel
              </Button>
            )}
            {!isSuccess && prevOption && (
              <Button
                variant="contained"
                onClick={() => handleOptionClick(prevOption)}
                disabled={isRetrying || isTimerExpired}
                disableElevation
                sx={{ flex: 1.5, borderRadius: 50, textTransform: 'none', py: 1.2, }}
                startIcon={isRetrying ? <CircularProgress size={20} color="inherit" /> : <ReplayIcon sx={{ fontSize: '1.1rem !important' }} />}
              >
                {isRetrying ? 'Retrying...' : `Retry ${prevOption.authMode}`}
              </Button>
            )}
            {isSuccess && (
              <Button
                variant="contained"
                onClick={handleBack}
                disableElevation
                sx={{ flex: 1.5, borderRadius: 50, textTransform: 'none', py: 1.2 }}
              >
                Done
              </Button>
            )}
          </Stack>

          {!isSuccess && !isLoadingAltOptions && (
            <Box width="100%" sx={{ pt: 4, borderTop: '1px solid #f0f0f0', mt: 3 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="subtitle2" fontWeight={700} color="text.primary">
                  Alternative Payment Options
                </Typography>
                {timeLeft !== null && (
                  <Box sx={{
                    bgcolor: '#F3F4F6',
                    px: 1.5,
                    py: 0.5,
                    borderRadius: 4,
                  }}>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <AccessTimeIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                      <Typography variant="caption" fontWeight={600} color="text.secondary">
                        {isTimerExpired ? 'Expired' : formatTime(timeLeft)}
                      </Typography>
                    </Stack>
                  </Box>
                )}
              </Stack>

              {newOptions && newOptions.length > 0 ? (
                <Stack spacing={2}>
                  {(() => {
                    if (!prevOption) return null;
                    const prevMode = prevOption.authMode;
                    let targetAuthMode = '';
                    if (prevMode === 'UPI') targetAuthMode = 'Net Banking';
                    else if (prevMode === 'Net Banking') targetAuthMode = 'UPI';

                    const option = newOptions.find((opt: any) => opt.authMode === targetAuthMode);
                    if (!option) return null;

                    const isUPI = option.authMode === 'UPI';
                    const modeDef = AUTH_MODES.find(m => m.label === option.authMode);

                    if (isUPI) {
                      return (
                        <Paper
                          key={option.id}
                          elevation={0}
                          sx={{
                            border: '1px solid #e0e0e0',
                            borderRadius: 3,
                            overflow: 'hidden',
                          }}
                        >
                          <Box
                            sx={{
                              p: 2,
                              cursor: (isRetrying || isVerifyingVpa || isTimerExpired) ? 'default' : 'pointer',
                              '&:hover': { bgcolor: (isRetrying || isVerifyingVpa || isTimerExpired) ? 'transparent' : '#FAFAFA' }
                            }}
                            onClick={() => {
                              if (!isRetrying && !isVerifyingVpa && !isTimerExpired) {
                                handleOptionClick(option);
                              }
                            }}
                          >
                            <Stack direction="row" alignItems="center" spacing={2}>
                              <Box sx={{
                                width: 40, height: 40, borderRadius: '50%', border: '1px solid #f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'primary.main', bgcolor: '#fff'
                              }}>
                                {modeDef?.icon}
                              </Box>
                              <Box flex={1} textAlign="left">
                                <Typography variant="body2" fontWeight={600} color="text.primary">
                                  Pay via UPI App
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  Tap to open app
                                </Typography>
                              </Box>
                              <KeyboardArrowRightIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                            </Stack>
                          </Box>

                          {/* <Divider /> */}

                          {/* <Box sx={{ p: 2 }}>
                            <Stack direction="row" alignItems="center" spacing={1} mb={1.5}>
                              <Box sx={{
                                width: 40, height: 40, borderRadius: '50%', border: '1px solid #f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'primary.main', bgcolor: '#fff'
                              }}>
                                <Typography variant="caption" fontWeight="bold" sx={{ fontSize: '0.75rem' }}>@</Typography>
                              </Box>
                              <Typography variant="body2" fontWeight={600} color="text.primary">
                                Pay via UPI ID
                              </Typography>
                            </Stack>

                            <Stack direction="row" spacing={1}>
                              <TextField
                                fullWidth
                                variant="outlined"
                                placeholder="username@bank"
                                size="small"
                                value={upiId}
                                onChange={(e) => handleUpiInputChange(e.target.value)}
                                disabled={isRetrying || isVerifyingVpa || isTimerExpired}
                                InputProps={{
                                  endAdornment: (
                                    <InputAdornment position="end">
                                      {isVerifyingVpa && <CircularProgress size={16} />}
                                      {!isVerifyingVpa && vpaAttempted && (
                                        isVpaVerified ? (
                                          <CheckCircleIcon sx={{ color: 'green', fontSize: 20 }} />
                                        ) : (
                                          <CancelIcon sx={{ color: 'red', fontSize: 20 }} />
                                        )
                                      )}
                                    </InputAdornment>
                                  ),
                                }}
                                sx={{
                                  '& .MuiOutlinedInput-root': {
                                    borderRadius: 2,
                                    bgcolor: '#fff',
                                    height: '40px',
                                  }
                                }}
                              />
                              <Button
                                variant="contained"
                                disableElevation
                                onClick={() => handleVerifyAndPay(option)}
                                disabled={!upiId || isRetrying || isVerifyingVpa || isTimerExpired}
                                sx={{
                                  borderRadius: 2,
                                  height: '40px',
                                  textTransform: 'none',
                                  whiteSpace: 'nowrap',
                                  bgcolor: '#4B4DED',
                                  px: 3,
                                  '&:hover': { bgcolor: '#3a3cc7' }
                                }}
                              >
                                {isRetrying ? <CircularProgress size={20} color="inherit" /> : (isVpaVerified ? 'Pay' : 'Verify')}
                              </Button>
                            </Stack>
                            <Box textAlign="left" mt={1}>
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                Use your VPA from any UPI app (e.g., oksbi, ybl)
                              </Typography>
                            </Box>
                          </Box> */}
                        </Paper>
                      );
                    }

                    // For non-UPI options:
                    const title = 'Net Banking';
                    const subtitle = 'All major banks supported';

                    return (
                      <Paper
                        key={option.id}
                        elevation={0}
                        onClick={() => handleOptionClick(option)}
                        sx={{
                          border: '1px solid #e0e0e0',
                          borderRadius: 3,
                          p: 2,
                          cursor: (isRetrying || isTimerExpired) ? 'default' : 'pointer',
                          opacity: (isRetrying || isTimerExpired) ? 0.6 : 1,
                          transition: 'all 0.2s',
                          '&:hover': {
                            borderColor: (isRetrying || isTimerExpired) ? '#e0e0e0' : 'primary.main',
                            bgcolor: (isRetrying || isTimerExpired) ? 'transparent' : '#FAFAFA'
                          }
                        }}
                      >
                        <Stack direction="row" alignItems="center" spacing={2}>
                          <Box sx={{
                            width: 40, height: 40, borderRadius: 2, bgcolor: '#eff2f7', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'primary.main'
                          }}>
                            {modeDef?.icon}
                          </Box>
                          <Box flex={1} textAlign="left">
                            <Typography variant="body2" fontWeight={600} color="text.primary">
                              {title}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {subtitle}
                            </Typography>
                          </Box>
                          <KeyboardArrowRightIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                        </Stack>
                      </Paper>
                    );
                  })()}
                  {isTimerExpired && (
                    <Typography variant="caption" color="error" textAlign="center">
                      Session expired
                    </Typography>
                  )}
                </Stack>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No other alternative options available
                </Typography>
              )}
            </Box>
          )}
        </Stack>
      </Paper>

      {/* Retry Confirmation Dialog */}
      <Dialog
        open={confirmOpen}
        onClose={handleConfirmClose}
        PaperProps={{
          sx: { borderRadius: 3, px: 1, py: 0.5 }
        }}
      >
        <DialogTitle sx={{ fontWeight: 700, fontSize: '1.1rem' }}>
          Confirm Retry
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: 'text.primary' }}>
            {confirmMessage}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={handleConfirmClose}
            variant="outlined"
            sx={{ borderRadius: 50, textTransform: 'none', fontWeight: 600 }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmProceed}
            variant="contained"
            disableElevation
            sx={{ borderRadius: 50, textTransform: 'none', fontWeight: 600 }}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
